// マスタ編集画面のタブと、CSV として出し入れするテーブルの対応表。
// 画面は13個、テーブルは20個あり、1つの画面が複数テーブルを束ねることがある。
// 本機能で唯一の手書き定義であり、これ以外の情報は全て自動生成物から導出する。

import type { SliceKey } from "../../../../api/dbSlice";

// CSVの1行目に置く識別行の、1列目に固定で入る見出し文字列。
// 2列目にはテーブル名(コレクションキー)が入り、取り込み時はこの行だけでどのマスタかを決める。
// 書き出しと取り込みで綴りがずれると取り込めなくなるため、定義はここ1か所に置く。
export const MASTER_CSV_TABLE_ROW_LABEL = "テーブル";

// マスタ編集画面のタブを識別するキー。URL のパスと同じ綴りを使う。
export type MasterScreenKey =
  | "area-division"
  | "material-division"
  | "client"
  | "work-master"
  | "work-classification"
  | "carry-in-location"
  | "location-division"
  | "quality-measurement-item"
  | "material-master"
  | "stirring-method"
  | "construction-method"
  | "pot-life"
  | "mix-ratio-master";

// 画面をまとめて並べるときの系統。マスタ編集画面のタブ分けと同じ区切り。
export type MasterSystemTier = "division" | "work" | "material";

export type MasterCsvTarget = {
  screenKey: MasterScreenKey;
  // 選択ポップアップに表示する日本語名
  label: string;
  system: MasterSystemTier;
  // この画面を選んだときに出力されるテーブル群
  collectionKeys: SliceKey[];
};

// 画面の並び順は既存のマスタ編集画面のタブ順に合わせる。
export const MASTER_CSV_TARGETS: readonly MasterCsvTarget[] = [
  { screenKey: "area-division", label: "工事型区分", system: "division", collectionKeys: ["areaDivisions", "areaDivisionLocationLabels"] },
  { screenKey: "material-division", label: "材料区分", system: "division", collectionKeys: ["materialDivisions"] },
  { screenKey: "client", label: "発注者", system: "division", collectionKeys: ["clients"] },
  { screenKey: "work-master", label: "工事マスタ", system: "work", collectionKeys: ["works", "workTechnicians"] },
  { screenKey: "work-classification", label: "工種 / 種別 / 工程", system: "work", collectionKeys: ["workTypes", "workSubtypes", "workDetails"] },
  { screenKey: "carry-in-location", label: "搬入場所", system: "work", collectionKeys: ["carryInLocations"] },
  { screenKey: "location-division", label: "場所区分", system: "work", collectionKeys: ["locationDivisions", "locationDivisionParentPaths"] },
  { screenKey: "quality-measurement-item", label: "品質測定項目", system: "work", collectionKeys: ["qualityMeasurementItems"] },
  { screenKey: "material-master", label: "材料マスタ", system: "material", collectionKeys: ["materials", "materialDetails"] },
  { screenKey: "stirring-method", label: "撹拌方法", system: "material", collectionKeys: ["stirringMethods"] },
  { screenKey: "construction-method", label: "施工方法", system: "material", collectionKeys: ["constructionMethods"] },
  { screenKey: "pot-life", label: "可使時間", system: "material", collectionKeys: ["potLifeSpecs"] },
  { screenKey: "mix-ratio-master", label: "配合比マスタ", system: "material", collectionKeys: ["mixRatioDefinitions", "mixRatioDetails"] },
];

// CSV の対象になり得る全テーブル。画面の並び順に沿って重複を除いたもの。
export const ALL_MASTER_CSV_COLLECTION_KEYS: readonly SliceKey[] = MASTER_CSV_TARGETS.flatMap((target) => target.collectionKeys).filter(
  (collectionKey, index, all) => all.indexOf(collectionKey) === index,
);

const TARGET_BY_SCREEN_KEY = new Map<MasterScreenKey, MasterCsvTarget>(MASTER_CSV_TARGETS.map((target) => [target.screenKey, target]));

// 選択された画面キー群を、実際に出力するテーブル群へ展開する。
// 同じテーブルを持つ画面が重複して選ばれても1回だけ返す。
export function collectionKeysOfScreenKeys(screenKeys: readonly MasterScreenKey[]): SliceKey[] {
  const collected: SliceKey[] = [];
  for (const screenKey of screenKeys) {
    const target = TARGET_BY_SCREEN_KEY.get(screenKey);
    if (target !== undefined) {
      for (const collectionKey of target.collectionKeys) {
        if (!collected.includes(collectionKey)) {
          collected.push(collectionKey);
        }
      }
    }
  }
  return collected;
}

// 画面キーを利用者向けの日本語名にする。対応する画面が無いキーはそのまま返す。
export function labelOfScreenKey(screenKey: MasterScreenKey): string {
  const target = TARGET_BY_SCREEN_KEY.get(screenKey);
  if (target !== undefined) {
    return target.label;
  }
  return screenKey;
}

// テーブル識別子から画面の日本語名を引くための対応表。
// サーバーやデータベースはテーブル識別子でしかマスタを示さないため、利用者に見せる前にここで
// マスタ編集画面の名前へ言い換える。1つの画面が複数テーブルを束ねている場合は画面名だけを返す。
const SCREEN_LABEL_BY_COLLECTION_KEY = new Map<string, string>(
  MASTER_CSV_TARGETS.flatMap((target) => target.collectionKeys.map((collectionKey) => [collectionKey, target.label] as const)),
);

// テーブル識別子を画面の日本語名へ言い換える。対応する画面が無い識別子はそのまま返す。
export function screenLabelOfCollectionKey(collectionKey: string): string {
  return SCREEN_LABEL_BY_COLLECTION_KEY.get(collectionKey) ?? collectionKey;
}
